/* eslint-disable react/jsx-indent */
import { withPrefix } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { SkipNavLink, SkipNavContent } from '@reach/skip-nav';

import SiteNavbar from './Navbar';
import Footer from './Footer';

import useSiteMetadata from './SiteMetadata';

require('@gouch/to-title-case');

export function ContentSection({ children, width, isZebraStriped, className }) {
  let maxWidth = 'max-w-2xl';
  if (width === 'mid') {
    maxWidth = 'max-w-3xl';
  } else if (width === 'wide') {
    maxWidth = 'max-w-7xl';
  }

  return (
    <section
      className={`px-4 py-12 md:p-20 ${isZebraStriped ? 'odd:bg-base-100 even:bg-base-200' : ''} ${className || ''}`}
    >
      <div className={`${maxWidth} mx-auto p-0`}>{children}</div>
    </section>
  );
}

ContentSection.propTypes = {
  children: PropTypes.node.isRequired,
  width: PropTypes.oneOf(['narrow', 'mid', 'wide']),
  isZebraStriped: PropTypes.bool,
  className: PropTypes.string,
};

ContentSection.defaultProps = {
  width: 'narrow',
  isZebraStriped: false,
};

function TemplateWrapper({
  title,
  description,
  image,
  embedPage,
  children,
  canonicalPath,
}) {
  const defaultMetadata = useSiteMetadata();
  const { siteUrl } = defaultMetadata;
  const pageTitle = title || defaultMetadata.title;
  const pageDescription = description || defaultMetadata.description;
  const pageImage = image || defaultMetadata.image;
  const vwoEnabled = process.env.GATSBY_VWO_ENABLED === 'true';

  return (
    <>
      <Helmet>
        <html lang='en' />
        <title>{pageTitle.toTitleCase()}</title>
        <meta name='description' content={pageDescription} />
        <meta name='og:description' content={pageDescription} />

        {vwoEnabled && (
          <link
            rel='preconnect'
            href='https://dev.visualwebsiteoptimizer.com'
          />
        )}
        {vwoEnabled && (
          <script type='text/javascript' id='vwoCode'>
            {`window._vwo_code || (function() {
              var account_id=781180,
              version=2.0,
              settings_tolerance=2000,
              hide_element='body',
              hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
              /* DO NOT EDIT BELOW THIS LINE */
              f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();`}
          </script>
        )}
        <script
          src='https://cdn.usefathom.com/script.js'
          data-site='NYVLDURK'
          data-spa='auto'
          defer
        />

        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href={`${withPrefix('/')}img/apple-touch-icon.png`}
        />
        <link
          rel='icon'
          type='image/png'
          href={`${withPrefix('/')}img/favicon-32x32.png`}
          sizes='32x32'
        />
        <link
          rel='icon'
          type='image/png'
          href={`${withPrefix('/')}img/favicon-16x16.png`}
          sizes='16x16'
        />

        <link
          rel='mask-icon'
          href={`${withPrefix('/')}img/safari-pinned-tab.svg`}
          color='#ff4400'
        />

        {canonicalPath && (
          <link rel='canonical' href={`${siteUrl}${canonicalPath}`} />
        )}

        <meta name='theme-color' content='#fff' />
        <meta property='og:type' content='website' />
        <meta property='og:title' content={pageTitle} />
        <meta property='fb:app_id' content='218198872691029' />
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:site' content='@VoteAmerica' />
        <meta property='og:image' content={`${siteUrl}/${pageImage}`} />
      </Helmet>
      {embedPage ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{children}</>
      ) : (
        <>
          <SkipNavLink className='sr-only' />
          <div className='flex min-h-screen flex-col bg-base-100 text-base-content'>
            <SiteNavbar />
            <SkipNavContent />
            <main className='flex-1'>{children}</main>
            <Footer />
          </div>
        </>
      )}
    </>
  );
}

TemplateWrapper.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  embedPage: PropTypes.bool,
  canonicalPath: PropTypes.string,
};

export default TemplateWrapper;
